import {
  Box,
  CircularProgress,
  LinearProgress,
  Typography,
} from '@mui/material';
import { useCallback, useRef } from 'react';

import {
  PreFlightCompanyData,
  PreFlightData,
  PreFlightListData,
} from '../../../../api/preFlight/types';
import { Constants } from '../../constants';
import { RequestCard } from './RequestCard';
import { CenteredBox, CompanyHeader, RequestsPanel } from './styles';

interface RequestsListProps {
  selectedCompany: PreFlightCompanyData | undefined;
  requests: PreFlightListData[];
  loading: boolean;
  selectedCompanyId: number | undefined;
  selectedPreflight: PreFlightData | undefined;
  onSelectRequest: (id: number) => void;
  page: number;
  totalPages: number;
  onLoadMore: () => void;
}

export function RequestsList({
  selectedCompany,
  requests,
  loading,
  selectedCompanyId,
  selectedPreflight,
  onSelectRequest,
  page,
  totalPages,
  onLoadMore,
}: RequestsListProps): JSX.Element {
  const observer = useRef<IntersectionObserver | null>(null);

  const lastElementRef = useCallback(
    (node: HTMLDivElement | null): void => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && page < totalPages) {
          onLoadMore();
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, page, totalPages, onLoadMore]
  );

  return (
    <RequestsPanel>
      {selectedCompany && (
        <CompanyHeader>
          <Typography fontSize={21} fontWeight={600} color="primary.main">
            {selectedCompany.name}
          </Typography>
          <Typography fontSize={14}>
            {selectedCompany.pending} {Constants.osPending}
          </Typography>
        </CompanyHeader>
      )}
      {loading && requests.length === 0 ? (
        <CenteredBox>
          <CircularProgress size={28} />
        </CenteredBox>
      ) : (
        <Box>
          {requests.length === 0 && selectedCompanyId ? (
            <CenteredBox>
              <Typography fontSize={13} color="text.secondary">
                {Constants.noRequests}
              </Typography>
            </CenteredBox>
          ) : (
            requests.map((req, index) => {
              const isLast = index === requests.length - 1;
              return (
                <div key={req.id} ref={isLast ? lastElementRef : undefined}>
                  <RequestCard
                    request={req}
                    isSelected={selectedPreflight?.id === req.id}
                    onClick={() => onSelectRequest(req.id)}
                  />
                </div>
              );
            })
          )}
          {loading && requests.length > 0 && (
            <LinearProgress sx={{ margin: '20px' }} />
          )}
        </Box>
      )}
    </RequestsPanel>
  );
}
